import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft, Users, Search, ChevronRight, MessageSquare, GitBranch, Lightbulb,
  Star, MapPin, DollarSign, Tag, User, Plus, Send, ThumbsUp, ThumbsDown,
  BarChart3, UserPlus, Vote, ChevronDown, Trash2, CheckCircle, Building2,
  Pencil, Clock, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import PDSurveys from './PDSurveys';
import PDAnalysis from './PDAnalysis';
import NeedsResourcesMap from './NeedsResourcesMap';
import PDWorkshops from './PDWorkshops';

const API = BACKEND_URL;

const STAGE_COLORS = {
  needs_mapping: '#6366F1', co_design: '#3B82F6', evaluation: '#8B5CF6',
  invitation: '#F59E0B', approval: '#10B981', launch: '#EF4444',
  monitoring: '#14B8A6', post_evaluation: '#F97316',
};

const STAKEHOLDER_COLORS = {
  citizens: '#3B82F6', local_authorities: '#10B981', ngos: '#8B5CF6',
  donors: '#F59E0B', private_sector: '#EF4444', research: '#6366F1', working_group: '#14B8A6',
};

const STAKEHOLDER_LABELS = {
  citizens: 'Citizens', local_authorities: 'Local Authorities', ngos: 'NGOs',
  donors: 'Donors', private_sector: 'Private Sector', research: 'Research', working_group: 'Working Group',
};

// ===== Stage Tracker =====
const StageTracker = ({ currentStage, stages, onAdvance, compact = false }) => {
  const currentIdx = stages.findIndex(s => s.id === currentStage);
  if (compact) {
    const stage = stages.find(s => s.id === currentStage) || stages[0];
    return (
      <Badge
        className="text-xs font-medium"
        style={{
          backgroundColor: stage.color + '22',           // ~13% tint for visual identity
          color: '#0f172a',                              // slate-900 text → 17.4:1 on near-white (clears AAA)
          borderLeft: `3px solid ${stage.color}`,        // colored left edge keeps the stage identity
          border: `1px solid ${stage.color}55`,
        }}
      >
        {stage.title}
      </Badge>
    );
  }
  return (
    <div className="space-y-2" data-testid="stage-tracker">
      <div className="flex items-center gap-1">
        {stages.map((stage, idx) => (
          <div key={stage.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${idx <= currentIdx ? 'text-white' : 'bg-gray-200 text-gray-500'}`}
                style={idx <= currentIdx ? { backgroundColor: stage.color } : {}}
                title={stage.title}
              >
                {idx < currentIdx ? <CheckCircle className="h-4 w-4" /> : idx + 1}
              </div>
              <span className="text-xs text-gray-500 mt-1 text-center leading-tight w-16 truncate">{stage.title}</span>
            </div>
            {idx < stages.length - 1 && (
              <div className={`h-0.5 flex-1 mx-0.5 ${idx < currentIdx ? 'bg-gray-800' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
      {onAdvance && currentIdx < stages.length - 1 && (
        <Button size="sm" variant="outline" className="text-xs" onClick={() => onAdvance(stages[currentIdx + 1].id)} data-testid="advance-stage-btn">
          Advance to: {stages[currentIdx + 1].title}
        </Button>
      )}
    </div>
  );
};

// ===== Influence/Interest Matrix =====
const StakeholderMatrix = ({ stakeholders }) => {
  if (stakeholders.length === 0) return null;
  const gridSize = 5;
  return (
    <div className="relative border rounded-lg p-4 bg-white" data-testid="stakeholder-matrix">
      <p className="text-xs text-gray-500 text-center mb-2 font-medium">Influence / Interest Matrix</p>
      <div className="relative" style={{ width: '100%', paddingBottom: '100%' }}>
        <div className="absolute inset-0">
          {/* Grid background */}
          <div className="absolute inset-0 grid grid-cols-5 grid-rows-5">
            {Array.from({ length: 25 }).map((_, i) => (
              <div key={i} className="border border-gray-100" />
            ))}
          </div>
          {/* Quadrant labels */}
          <span className="absolute top-1 left-1 text-xs text-gray-300">Keep Satisfied</span>
          <span className="absolute top-1 right-1 text-xs text-gray-300 text-right">Key Players</span>
          <span className="absolute bottom-1 left-1 text-xs text-gray-300">Monitor</span>
          <span className="absolute bottom-1 right-1 text-xs text-gray-300 text-right">Keep Informed</span>
          {/* Stakeholder dots */}
          {stakeholders.map(sh => {
            const x = ((sh.interest - 1) / (gridSize - 1)) * 90 + 5;
            const y = (1 - (sh.influence - 1) / (gridSize - 1)) * 90 + 5;
            const color = STAKEHOLDER_COLORS[sh.stakeholder_type] || '#888';
            return (
              <div
                key={sh.id}
                className="absolute w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center cursor-default"
                style={{ left: `${x}%`, top: `${y}%`, backgroundColor: color, transform: 'translate(-50%, -50%)' }}
                title={`${sh.name} (${STAKEHOLDER_LABELS[sh.stakeholder_type] || sh.stakeholder_type})\nInfluence: ${sh.influence}/5 | Interest: ${sh.interest}/5`}
              >
                <span className="text-xs text-white font-bold">{sh.name.charAt(0)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>Low Interest</span><span>High Interest</span>
      </div>
      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 rotate-[-90deg] text-xs text-gray-500">Influence</div>
    </div>
  );
};

// ===== Poll Card =====
const PollCard = ({ poll, onVote }) => {
  const [selected, setSelected] = useState([]);
  const totalVotes = poll.options.reduce((s, o) => s + o.votes, 0);
  const hasVoted = poll.user_voted;

  const handleVote = () => {
    if (selected.length === 0) return;
    onVote(poll.id, selected);
  };

  return (
    <Card className="border-indigo-100" data-testid={`poll-${poll.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">{poll.question}</p>
            {poll.description && <p className="text-xs text-gray-500">{poll.description}</p>}
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-xs capitalize">{poll.poll_type}</Badge>
            <Badge className={`text-xs ${poll.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{poll.status}</Badge>
          </div>
        </div>
        <div className="space-y-1.5 mt-3">
          {poll.options.map((opt, i) => {
            const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
            const isSelected = selected.includes(i);
            return (
              <div key={i} className="relative">
                {!hasVoted && poll.status === 'open' ? (
                  <button
                    onClick={() => {
                      if (poll.poll_type === 'single') setSelected([i]);
                      else setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
                    }}
                    className={`w-full text-left p-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-gray-200 hover:border-indigo-200'}`}
                    data-testid={`poll-option-${i}`}
                  >
                    {opt.text}
                  </button>
                ) : (
                  <div className="relative p-2 rounded-lg border bg-gray-50 overflow-hidden">
                    <div className="absolute inset-0 bg-indigo-100 transition-all" style={{ width: `${pct}%` }} />
                    <div className="relative flex justify-between text-sm">
                      <span className="text-gray-800">{opt.text}</span>
                      <span className="font-medium text-gray-700">{pct}% ({opt.votes})</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-gray-500">{totalVotes} vote{totalVotes !== 1 ? 's' : ''} · by {poll.created_by}</span>
          {!hasVoted && poll.status === 'open' && (
            <Button size="sm" onClick={handleVote} disabled={selected.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cast-vote-btn">
              <Vote className="h-3 w-3 mr-1" /> Vote
            </Button>
          )}
          {hasVoted && <Badge className="bg-green-100 text-green-700 text-xs">Voted</Badge>}
        </div>
      </CardContent>
    </Card>
  );
};

// ===== MAIN COMPONENT =====
const ParticipatoryPlanning = ({ user }) => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [stages, setStages] = useState([]);
  const [stakeholderTypes, setStakeholderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editIdea, setEditIdea] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [showStakeholderDialog, setShowStakeholderDialog] = useState(false);
  const [newIdea, setNewIdea] = useState({ title: '', description: '', category: '', budget: '', location: '', country: 'Greece', social_actor_id: '' });
  const [newComment, setNewComment] = useState('');
  const [newPoll, setNewPoll] = useState({ question: '', description: '', poll_type: 'single', options: ['', ''] });
  const [newStakeholder, setNewStakeholder] = useState({ name: '', stakeholder_type: 'citizens', role: '', influence: 3, interest: 3, contact: '' });
  // Registered Social Actors — powers the "Proposed by organisation" picker
  // so each idea shows which organisation from the Registry generated it.
  const [socialActors, setSocialActors] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/social-actors`, { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.actors || []);
        setSocialActors(list);
      } catch {
        setSocialActors([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Locate the social-actor profile owned by the logged-in user (if any) so
  // we can preselect & lock the "Proposed by" field for social_actor users.
  const myActor = (user?.role === 'social_actor')
    ? socialActors.find((a) => a.user_id === user.id)
    : null;
  const lockProposedBy = !!myActor;
  useEffect(() => {
    if (showCreateDialog && lockProposedBy && newIdea.social_actor_id !== myActor.id) {
      setNewIdea((prev) => ({ ...prev, social_actor_id: myActor.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateDialog, lockProposedBy, myActor?.id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ideasRes, metaRes] = await Promise.all([
        axios.get(`${API}/api/participatory-design/ideas`, { headers }),
        axios.get(`${API}/api/participatory-design/lifecycle-stages`, { headers }),
      ]);
      setIdeas(ideasRes.data || []);
      setStages(metaRes.data.stages || []);
      setStakeholderTypes(metaRes.data.stakeholder_types || []);
    } catch {
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/participatory-design/seed`, {}, { headers });
      toast.success('Sample data seeded');
      fetchAll();
    } catch { toast.error('Failed to seed'); }
  };

  const handleCreateIdea = async () => {
    if (!newIdea.title || !newIdea.description || !newIdea.category) { toast.error('Title, description and category required'); return; }
    try {
      await axios.post(`${API}/api/participatory-design/ideas`, {
        ...newIdea,
        budget: newIdea.budget ? parseFloat(newIdea.budget) : null,
        social_actor_id: newIdea.social_actor_id || null,
      }, { headers });
      toast.success('Idea created');
      setShowCreateDialog(false);
      setNewIdea({ title: '', description: '', category: '', budget: '', location: '', country: 'Greece', social_actor_id: '' });
      fetchAll();
    } catch { toast.error('Failed to create idea'); }
  };

  const handleSelectIdea = async (ideaId) => {
    try {
      const res = await axios.get(`${API}/api/participatory-design/ideas/${ideaId}`, { headers });
      setSelectedIdea(res.data);
    } catch { toast.error('Failed to load idea'); }
  };

  // ── Edit Idea (RBAC-aware) ──────────────────────────────────────────────
  // Owner social-actors get a "pending changes" stash; Admin/PM apply changes
  // immediately. Edits happen against the same PUT /ideas/{id} endpoint which
  // returns `_pending: True` when the change is queued.
  const openEditDialog = () => {
    if (!selectedIdea) return;
    setEditIdea({
      title: selectedIdea.title || '',
      description: selectedIdea.description || '',
      category: selectedIdea.category || '',
      budget: selectedIdea.budget != null ? String(selectedIdea.budget) : '',
      location: selectedIdea.location || '',
      country: selectedIdea.country || 'Greece',
      social_actor_id: selectedIdea.social_actor_id || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedIdea || !editIdea) return;
    if (!editIdea.title || !editIdea.description || !editIdea.category) {
      toast.error('Title, description and category required');
      return;
    }
    try {
      const res = await axios.put(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}`,
        {
          ...editIdea,
          budget: editIdea.budget ? parseFloat(editIdea.budget) : null,
          social_actor_id: editIdea.social_actor_id || null,
        },
        { headers },
      );
      const pending = res.data && res.data._pending;
      if (pending) {
        toast.success('Changes submitted — awaiting administrator approval');
      } else {
        toast.success('Idea updated');
      }
      setShowEditDialog(false);
      setEditIdea(null);
      // Refresh detail + list so the banner / fields are up to date
      const fresh = await axios.get(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}`,
        { headers },
      );
      setSelectedIdea(fresh.data);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update idea');
    }
  };

  const handleApprovePending = async () => {
    if (!selectedIdea) return;
    try {
      await axios.post(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}/pending-changes/approve`,
        {},
        { headers },
      );
      toast.success('Pending changes approved');
      const fresh = await axios.get(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}`,
        { headers },
      );
      setSelectedIdea(fresh.data);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectPending = async () => {
    if (!selectedIdea) return;
    try {
      await axios.post(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}/pending-changes/reject`,
        { reason: rejectReason || '' },
        { headers },
      );
      toast.success('Pending changes rejected');
      setRejectReason('');
      const fresh = await axios.get(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}`,
        { headers },
      );
      setSelectedIdea(fresh.data);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleVoteIdea = async (vote) => {
    if (!selectedIdea) return;
    try {
      const currentVote = selectedIdea.user_vote;
      const newVote = currentVote === vote ? 'none' : vote;
      const res = await axios.post(`${API}/api/participatory-design/ideas/${selectedIdea.id}/vote`, { vote: newVote }, { headers });
      setSelectedIdea(prev => ({ ...prev, upvotes: res.data.upvotes, downvotes: res.data.downvotes, user_vote: res.data.user_vote }));
      fetchAll();
    } catch { toast.error('Failed to vote'); }
  };

  const handleAdvanceStage = async (newStage) => {
    if (!selectedIdea) return;
    try {
      const res = await axios.patch(`${API}/api/participatory-design/ideas/${selectedIdea.id}/stage`, { stage: newStage }, { headers });
      setSelectedIdea(prev => ({ ...prev, stage: res.data.stage, stage_history: res.data.stage_history }));
      toast.success(`Advanced to ${stages.find(s => s.id === newStage)?.title || newStage}`);
      fetchAll();
    } catch { toast.error('Failed to advance stage'); }
  };

  const handleTransferToInvestment = async () => {
    if (!selectedIdea) return;
    const ok = window.confirm(
      'Transfer this idea to the Social Investment Registry?\n\n' +
      'A new draft investment will be created carrying over the title, description, budget, ' +
      'location, organisation and category. This action can only be performed once.'
    );
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API}/api/participatory-design/ideas/${selectedIdea.id}/transfer-to-investment`,
        {},
        { headers }
      );
      const newId = res.data?.investment_id;
      const newTitle = res.data?.investment?.title || selectedIdea.title;
      setSelectedIdea(prev => ({
        ...prev,
        transferred_to_investment_id: newId,
        transferred_to_investment_title: newTitle,
        transferred_at: new Date().toISOString(),
        transferred_by: user?.username,
      }));
      toast.success('Transferred to Social Investment Registry');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to transfer idea');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIdea) return;
    try {
      await axios.post(`${API}/api/participatory-design/ideas/${selectedIdea.id}/comments`, { content: newComment }, { headers });
      setNewComment('');
      handleSelectIdea(selectedIdea.id);
      fetchAll();
    } catch { toast.error('Failed to add comment'); }
  };

  const handleCreatePoll = async () => {
    if (!newPoll.question || !selectedIdea) return;
    const opts = newPoll.options.filter(o => o.trim());
    if (opts.length < 2) { toast.error('At least 2 options required'); return; }
    try {
      await axios.post(`${API}/api/participatory-design/ideas/${selectedIdea.id}/polls`, { ...newPoll, options: opts }, { headers });
      toast.success('Poll created');
      setShowPollDialog(false);
      setNewPoll({ question: '', description: '', poll_type: 'single', options: ['', ''] });
      handleSelectIdea(selectedIdea.id);
    } catch { toast.error('Failed to create poll'); }
  };

  const handleVotePoll = async (pollId, optionIndices) => {
    try {
      await axios.post(`${API}/api/participatory-design/polls/${pollId}/vote`, { option_indices: optionIndices }, { headers });
      toast.success('Vote cast');
      handleSelectIdea(selectedIdea.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to vote'); }
  };

  const handleAddStakeholder = async () => {
    if (!newStakeholder.name || !selectedIdea) return;
    try {
      await axios.post(`${API}/api/participatory-design/ideas/${selectedIdea.id}/stakeholders`, newStakeholder, { headers });
      toast.success('Stakeholder added');
      setShowStakeholderDialog(false);
      setNewStakeholder({ name: '', stakeholder_type: 'citizens', role: '', influence: 3, interest: 3, contact: '' });
      handleSelectIdea(selectedIdea.id);
    } catch { toast.error('Failed to add stakeholder'); }
  };

  const handleRemoveStakeholder = async (shId) => {
    if (!selectedIdea) return;
    try {
      await axios.delete(`${API}/api/participatory-design/ideas/${selectedIdea.id}/stakeholders/${shId}`, { headers });
      handleSelectIdea(selectedIdea.id);
    } catch { toast.error('Failed to remove'); }
  };

  const filtered = ideas.filter(i => {
    if (stageFilter !== 'all' && i.stage !== stageFilter) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return i.title?.toLowerCase().includes(s) || i.description?.toLowerCase().includes(s);
  });

  const formatBudget = (amount) => amount ? `EUR ${new Intl.NumberFormat('el-GR', { maximumFractionDigits: 0 }).format(amount)}` : null;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;

  // ========== DETAIL VIEW ==========
  if (selectedIdea) {
    const isInvestor = user?.role === 'investor';
    const canManage = !isInvestor && (user?.role === 'administrator' || user?.role === 'project_manager' || user?.id === selectedIdea.author_id);
    return (
      <div className="space-y-4 p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => { setSelectedIdea(null); fetchAll(); }} data-testid="back-to-list-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{selectedIdea.title}</h1>
              <p className="text-xs text-gray-500">by {selectedIdea.author_name} · {selectedIdea.category}</p>
              {selectedIdea.social_actor_name && (
                <button
                  type="button"
                  onClick={() => navigate(`/social-actor-registry?focus=${selectedIdea.social_actor_id}`)}
                  className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                  title="Open the proposing organisation in the Social Actor Registry"
                  data-testid="idea-proposing-org-badge"
                >
                  <Building2 className="h-3 w-3" /> Proposed by {selectedIdea.social_actor_name}
                  {selectedIdea.investment_id && <span className="text-gray-500 ml-1">· linked to SI Registry</span>}
                </button>
              )}
            </div>
          </div>
          {/* Voting */}
          <div className="flex items-center gap-2" data-testid="idea-vote-section">
            <Button size="sm" variant={selectedIdea.user_vote === 'up' ? 'default' : 'outline'} onClick={() => handleVoteIdea('up')} className={selectedIdea.user_vote === 'up' ? 'bg-green-700 hover:bg-green-800' : ''} data-testid="upvote-btn">
              <ThumbsUp className="h-4 w-4 mr-1" /> {selectedIdea.upvotes || 0}
            </Button>
            <Button size="sm" variant={selectedIdea.user_vote === 'down' ? 'default' : 'outline'} onClick={() => handleVoteIdea('down')} className={selectedIdea.user_vote === 'down' ? 'bg-red-600 hover:bg-red-700' : ''} data-testid="downvote-btn">
              <ThumbsDown className="h-4 w-4 mr-1" /> {selectedIdea.downvotes || 0}
            </Button>
            {(() => {
              const isOwnerActor = user?.role === 'social_actor'
                && myActor && selectedIdea.social_actor_id === myActor.id;
              const canEdit = !isInvestor && (canManage || isOwnerActor);
              if (!canEdit) return null;
              return (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openEditDialog}
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="edit-idea-btn"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              );
            })()}
          </div>
        </div>

        {/* Stage Tracker */}
        <Card><CardContent className="p-4">
          <StageTracker currentStage={selectedIdea.stage} stages={stages} onAdvance={canManage ? handleAdvanceStage : null} />
        </CardContent></Card>

        {isInvestor && (
          <div
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="investor-view-only-banner"
          >
            <User className="h-3.5 w-3.5" />
            <span>View-only access: as an Investor you can browse and vote on Participatory-Design ideas, but cannot create or edit them.</span>
          </div>
        )}

        {/* Pending-changes banner — visible to everyone, with Approve / Reject
            controls for Admin / PM. Mirrors the same pattern used by the
            Social-Actor and Social-Investment-Registry modules. */}
        {selectedIdea.pending_changes && Object.keys(selectedIdea.pending_changes).length > 0 && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
            data-testid="idea-pending-changes-banner"
          >
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900">
                  Pending changes awaiting review
                </p>
                <p className="text-amber-800 mt-0.5">
                  Edits submitted
                  {selectedIdea.pending_changes_by ? ` by ${selectedIdea.pending_changes_by}` : ''}
                  {selectedIdea.pending_changes_at
                    ? ` on ${new Date(selectedIdea.pending_changes_at).toLocaleDateString('en-GB')}`
                    : ''}{' '}
                  are queued for administrator approval. The live idea is unchanged until the changes are approved.
                </p>
                <ul className="mt-2 text-xs text-amber-900 space-y-0.5">
                  {Object.entries(selectedIdea.pending_changes)
                    .filter(([k]) => !['updated_at', 'pending_changes_at', 'pending_changes_by'].includes(k))
                    .slice(0, 6)
                    .map(([k, v]) => (
                      <li key={k} className="font-mono">
                        <span className="text-amber-700">{k}:</span>{' '}
                        <span className="text-amber-900">
                          {typeof v === 'string' ? (v.length > 80 ? v.slice(0, 80) + '…' : v) : JSON.stringify(v)}
                        </span>
                      </li>
                    ))}
                </ul>
                {canManage && (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Input
                      placeholder="Optional rejection reason"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="h-8 text-xs max-w-xs bg-white border-amber-200"
                      data-testid="reject-reason-input"
                    />
                    <Button
                      size="sm"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs"
                      onClick={handleApprovePending}
                      data-testid="approve-pending-btn"
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50 h-8 text-xs"
                      onClick={handleRejectPending}
                      data-testid="reject-pending-btn"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transferred to Social Investment Registry banner (once promoted) */}
        {selectedIdea.transferred_to_investment_id && (
          <div
            className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
            data-testid="idea-transferred-banner"
          >
            <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-emerald-800">
                Transferred to the Social Investment Registry
              </p>
              <p className="text-emerald-700 mt-0.5">
                A draft investment was created
                {selectedIdea.transferred_by ? ` by ${selectedIdea.transferred_by}` : ''}
                {selectedIdea.transferred_at
                  ? ` on ${new Date(selectedIdea.transferred_at).toLocaleDateString('en-GB')}`
                  : ''}.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              onClick={() => navigate(`/social-investment-registration?open=${selectedIdea.transferred_to_investment_id}`)}
              data-testid="open-transferred-investment-btn"
            >
              Open investment <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}

        {/* Transfer to Social Investment Registry — Admin/PM only, once stage reaches Post-Evaluation */}
        {!selectedIdea.transferred_to_investment_id
          && selectedIdea.stage === 'post_evaluation'
          && (user?.role === 'administrator' || user?.role === 'project_manager') && (
          <div
            className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3"
            data-testid="transfer-to-investment-callout"
          >
            <Building2 className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-indigo-800">
                Ready to promote this idea
              </p>
              <p className="text-indigo-700 mt-0.5">
                Post-Evaluation is complete. You can now transfer this idea into the
                Social Investment Registry as a new draft investment.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleTransferToInvestment}
              data-testid="transfer-to-investment-btn"
            >
              Transfer to SI Registry <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        )}

        <p className="text-sm text-gray-600">{selectedIdea.description}</p>
        <div className="flex flex-wrap gap-3 text-sm text-gray-500">
          {selectedIdea.budget && <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{formatBudget(selectedIdea.budget)}</span>}
          {selectedIdea.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{selectedIdea.location}, {selectedIdea.country}</span>}
        </div>

        <Tabs defaultValue="discussion">
          <TabsList className="flex-wrap">
            <TabsTrigger value="discussion" data-testid="tab-discussion">Discussion ({selectedIdea.comment_count})</TabsTrigger>
            <TabsTrigger value="polls" data-testid="tab-polls">Polls ({selectedIdea.poll_count})</TabsTrigger>
            <TabsTrigger value="stakeholders" data-testid="tab-stakeholders">Stakeholders ({selectedIdea.stakeholder_count})</TabsTrigger>
            <TabsTrigger value="collaboration" data-testid="tab-collab">Collaboration ({selectedIdea.request_count})</TabsTrigger>
            <TabsTrigger value="surveys" data-testid="tab-surveys">Surveys ({selectedIdea.survey_count || 0})</TabsTrigger>
            <TabsTrigger value="workshops" data-testid="tab-workshops">Workshops ({selectedIdea.workshop_count || 0})</TabsTrigger>
            <TabsTrigger value="analysis" data-testid="tab-analysis">Analysis</TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map">Needs & Resources Map</TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="tab-evals">Evaluations ({selectedIdea.evaluation_count})</TabsTrigger>
          </TabsList>

          {/* Discussion */}
          <TabsContent value="discussion">
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddComment()} placeholder="Share your thoughts..." data-testid="comment-input" />
                <Button size="sm" onClick={handleAddComment} data-testid="submit-comment-btn"><Send className="h-4 w-4" /></Button>
              </div>
              {(selectedIdea.comments || []).map(c => (
                <div key={c.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="text-gray-800">{c.content}</p>
                  <p className="text-xs text-gray-500 mt-1">by {c.author_name} · {new Date(c.created_at).toLocaleDateString('en-GB')}</p>
                </div>
              ))}
              {(selectedIdea.sub_ideas || []).length > 0 && (
                <div className="pt-3 border-t">
                  <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" /> Related Ideas</h4>
                  {selectedIdea.sub_ideas.map(s => (
                    <div key={s.id} className="bg-amber-50 rounded-lg p-3 text-sm mb-1.5">
                      <p className="text-gray-800">{s.content}</p>
                      <p className="text-xs text-gray-500 mt-1">by {s.author_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Polls */}
          <TabsContent value="polls">
            <div className="space-y-3">
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => setShowPollDialog(true)} data-testid="create-poll-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Create Poll</Button>
              )}
              {(selectedIdea.polls || []).length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No polls yet. Create one to gather community input.</p>
              ) : (
                (selectedIdea.polls || []).map(p => <PollCard key={p.id} poll={p} onVote={handleVotePoll} />)
              )}
            </div>
          </TabsContent>

          {/* Stakeholders */}
          <TabsContent value="stakeholders">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{(selectedIdea.stakeholders || []).length} stakeholders mapped</span>
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => setShowStakeholderDialog(true)} data-testid="add-stakeholder-btn"><UserPlus className="h-3.5 w-3.5 mr-1" /> Add Stakeholder</Button>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Matrix */}
                <StakeholderMatrix stakeholders={selectedIdea.stakeholders || []} />
                {/* List */}
                <div className="space-y-2">
                  {(selectedIdea.stakeholders || []).map(sh => (
                    <div key={sh.id} className="flex items-center justify-between p-2 rounded-lg border bg-gray-50" data-testid={`sh-${sh.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STAKEHOLDER_COLORS[sh.stakeholder_type] || '#888' }} />
                        <div>
                          <p className="text-xs font-medium text-gray-800">{sh.name}</p>
                          <p className="text-xs text-gray-500">{STAKEHOLDER_LABELS[sh.stakeholder_type]} · {sh.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Inf:{sh.influence} Int:{sh.interest}</span>
                        {canManage && <Button size="sm" variant="ghost" className="text-red-400 h-6" onClick={() => handleRemoveStakeholder(sh.id)}><Trash2 className="h-3 w-3" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(STAKEHOLDER_LABELS).map(([k, label]) => (
                  <div key={k} className="flex items-center gap-1 text-xs text-gray-500">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STAKEHOLDER_COLORS[k] }} />{label}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Collaboration */}
          <TabsContent value="collaboration">
            {(selectedIdea.requests || []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No collaboration requests yet.</p>
            ) : (
              <div className="space-y-2">
                {selectedIdea.requests.map(r => (
                  <div key={r.id} className="bg-blue-50 rounded-lg p-3 text-sm">
                    <p className="text-gray-800">{r.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{r.organization_name || r.requester_name} · <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge></p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Surveys */}
          <TabsContent value="surveys">
            <PDSurveys ideaId={selectedIdea.id} surveys={selectedIdea.surveys || []} canManage={canManage} />
          </TabsContent>

          {/* Workshops */}
          <TabsContent value="workshops">
            <PDWorkshops ideaId={selectedIdea.id} workshops={selectedIdea.workshops || []} canManage={canManage} userId={user?.id} />
          </TabsContent>

          {/* SWOT / PESTEL Analysis */}
          <TabsContent value="analysis">
            <PDAnalysis ideaId={selectedIdea.id} initialSwot={selectedIdea.swot} initialPestel={selectedIdea.pestel} canManage={canManage} />
          </TabsContent>

          {/* Needs & Resources Map */}
          <TabsContent value="map">
            <NeedsResourcesMap ideaId={selectedIdea.id} ideaTitle={selectedIdea.title} canManage={canManage} />
          </TabsContent>

          {/* Evaluations */}
          <TabsContent value="evaluations">
            {(selectedIdea.evaluations || []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No evaluations yet.</p>
            ) : (
              <div className="space-y-2">
                {selectedIdea.evaluations.map(ev => (
                  <div key={ev.id} className="bg-green-50 rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < ev.score ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />)}
                    </div>
                    <p className="text-gray-800">{ev.feedback}</p>
                    <p className="text-xs text-gray-500 mt-1">by {ev.evaluator_name}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Poll Dialog */}
        <Dialog open={showPollDialog} onOpenChange={setShowPollDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create Poll</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div><Label>Question *</Label><Input value={newPoll.question} onChange={e => setNewPoll({ ...newPoll, question: e.target.value })} placeholder="What should we decide?" data-testid="poll-question-input" /></div>
              <div><Label>Description</Label><Input value={newPoll.description} onChange={e => setNewPoll({ ...newPoll, description: e.target.value })} placeholder="Optional context" /></div>
              <div>
                <Label>Type</Label>
                <Select value={newPoll.poll_type} onValueChange={v => setNewPoll({ ...newPoll, poll_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Choice</SelectItem>
                    <SelectItem value="multi">Multiple Choice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1"><Label>Options</Label><Button size="sm" variant="ghost" onClick={() => setNewPoll({ ...newPoll, options: [...newPoll.options, ''] })}><Plus className="h-3 w-3 mr-1" />Add</Button></div>
                {newPoll.options.map((opt, i) => (
                  <div key={i} className="flex gap-1 mb-1">
                    <Input value={opt} onChange={e => { const o = [...newPoll.options]; o[i] = e.target.value; setNewPoll({ ...newPoll, options: o }); }} placeholder={`Option ${i + 1}`} className="h-8 text-sm" />
                    {newPoll.options.length > 2 && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setNewPoll({ ...newPoll, options: newPoll.options.filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3" /></Button>}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowPollDialog(false)}>Cancel</Button><Button onClick={handleCreatePoll} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-poll-btn">Create Poll</Button></div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Stakeholder Dialog */}
        <Dialog open={showStakeholderDialog} onOpenChange={setShowStakeholderDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Stakeholder</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div><Label>Name *</Label><Input value={newStakeholder.name} onChange={e => setNewStakeholder({ ...newStakeholder, name: e.target.value })} placeholder="Organization or group name" data-testid="sh-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={newStakeholder.stakeholder_type} onValueChange={v => setNewStakeholder({ ...newStakeholder, stakeholder_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STAKEHOLDER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Role</Label><Input value={newStakeholder.role} onChange={e => setNewStakeholder({ ...newStakeholder, role: e.target.value })} placeholder="e.g., Coordinator" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Influence (1-5)</Label><Input type="number" min={1} max={5} value={newStakeholder.influence} onChange={e => setNewStakeholder({ ...newStakeholder, influence: parseInt(e.target.value) || 3 })} /></div>
                <div><Label>Interest (1-5)</Label><Input type="number" min={1} max={5} value={newStakeholder.interest} onChange={e => setNewStakeholder({ ...newStakeholder, interest: parseInt(e.target.value) || 3 })} /></div>
              </div>
              <div><Label>Contact</Label><Input value={newStakeholder.contact} onChange={e => setNewStakeholder({ ...newStakeholder, contact: e.target.value })} placeholder="Email or phone" /></div>
              <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowStakeholderDialog(false)}>Cancel</Button><Button onClick={handleAddStakeholder} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-sh-btn">Add Stakeholder</Button></div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Idea Dialog (lifted here so it renders inside the detail view) */}
        <Dialog open={showEditDialog} onOpenChange={(o) => { setShowEditDialog(o); if (!o) setEditIdea(null); }}>
          <DialogContent className="max-w-lg" data-testid="edit-idea-dialog">
            <DialogHeader><DialogTitle>Edit Idea</DialogTitle></DialogHeader>
            {editIdea && (
              <div className="space-y-4 pt-2">
                {user?.role === 'social_actor' && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Your edits will be queued for administrator approval before going live.
                  </div>
                )}
                <div><Label>Title *</Label><Input value={editIdea.title} onChange={e => setEditIdea({ ...editIdea, title: e.target.value })} data-testid="edit-idea-title-input" /></div>
                <div><Label>Description *</Label><Textarea value={editIdea.description} onChange={e => setEditIdea({ ...editIdea, description: e.target.value })} rows={3} data-testid="edit-idea-description-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Category *</Label><Input value={editIdea.category} onChange={e => setEditIdea({ ...editIdea, category: e.target.value })} data-testid="edit-idea-category-input" /></div>
                  <div><Label>Budget (EUR)</Label><Input type="number" value={editIdea.budget} onChange={e => setEditIdea({ ...editIdea, budget: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Location</Label><Input value={editIdea.location} onChange={e => setEditIdea({ ...editIdea, location: e.target.value })} /></div>
                  <div><Label>Country</Label><Input value={editIdea.country} onChange={e => setEditIdea({ ...editIdea, country: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Proposed by organisation (Social Actor)</Label>
                  {user?.role === 'social_actor' ? (
                    <div
                      className="w-full mt-1 flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm"
                      data-testid="edit-idea-proposed-by-locked"
                    >
                      <span className="font-medium text-slate-800 truncate">
                        {socialActors.find((a) => a.id === editIdea.social_actor_id)?.organization_name || '—'}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">locked</span>
                    </div>
                  ) : (
                    <select
                      value={editIdea.social_actor_id || ''}
                      onChange={(e) => setEditIdea({ ...editIdea, social_actor_id: e.target.value })}
                      className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="edit-idea-social-actor-select"
                    >
                      <option value="">— Not linked to a registered organisation —</option>
                      {socialActors.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.organization_name}{a.city ? ` · ${a.city}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditIdea(null); }}>Cancel</Button>
                  <Button onClick={handleSaveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-idea-btn">
                    {user?.role === 'social_actor' ? 'Submit for approval' : 'Save changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Dashboard</Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="h-7 w-7 text-indigo-600" /> Participatory Design</h1>
            <p className="text-gray-600 mt-1">Co-design social investments through collaboration, voting, and stakeholder engagement</p>
          </div>
        </div>
        {user?.role !== 'investor' && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-idea-btn"><Plus className="h-4 w-4 mr-2" /> New Idea</Button>
        )}
      </div>

      {/* Filters */}
      <Card><CardContent className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
            <label htmlFor="pd-ideas-search" className="sr-only">Search ideas</label>
            <Input id="pd-ideas-search" placeholder="Search ideas..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-ideas" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <Button size="sm" variant={stageFilter === 'all' ? 'default' : 'outline'} onClick={() => setStageFilter('all')} className={stageFilter === 'all' ? 'bg-gray-900' : ''}>All</Button>
            {stages.map(s => (
              <Button key={s.id} size="sm" variant={stageFilter === s.id ? 'default' : 'outline'} onClick={() => setStageFilter(s.id)}
                className={stageFilter === s.id ? '' : ''} style={stageFilter === s.id ? { backgroundColor: s.color } : {}} data-testid={`filter-${s.id}`}>
                {s.title}
              </Button>
            ))}
          </div>
        </div>
      </CardContent></Card>

      {/* Ideas List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-16">
          <Lightbulb className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Ideas Yet</h3>
          <p className="text-gray-500 text-center max-w-md mb-4">Share an investment idea for collaborative design.</p>
          <div className="flex gap-3">
            <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">Create First Idea</Button>
            {(user?.role === 'administrator' || user?.role === 'project_manager') && <Button onClick={handleSeed} variant="outline" data-testid="seed-pd-btn">Load Sample Data</Button>}
          </div>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(idea => (
            <Card key={idea.id} data-testid={`idea-${idea.id}`} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleSelectIdea(idea.id)}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {/* Vote counts */}
                  <div className="flex flex-col items-center gap-0.5 shrink-0 pt-1 min-w-[40px]">
                    <ThumbsUp className="h-4 w-4 text-green-700" aria-hidden="true" />
                    <span className="text-sm font-bold text-green-800">{idea.upvotes || 0}</span>
                    <span className="text-xs font-medium text-red-700">{idea.downvotes || 0}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StageTracker currentStage={idea.stage} stages={stages} compact />
                      {idea.category && <Badge variant="secondary" className="text-xs">{idea.category}</Badge>}
                      {idea.poll_count > 0 && <Badge className="text-xs bg-indigo-100 text-indigo-700"><Vote className="h-2.5 w-2.5 mr-0.5" />{idea.poll_count} poll{idea.poll_count > 1 ? 's' : ''}</Badge>}
                      {idea.survey_count > 0 && <Badge className="text-xs bg-cyan-100 text-cyan-700">{idea.survey_count} survey{idea.survey_count > 1 ? 's' : ''}</Badge>}
                      {idea.has_swot && <Badge className="text-xs bg-green-100 text-green-700">SWOT</Badge>}
                      {idea.has_pestel && <Badge className="text-xs bg-violet-100 text-violet-700">PESTEL</Badge>}
                      {idea.workshop_count > 0 && <Badge className="text-xs bg-orange-100 text-orange-700">{idea.workshop_count} event{idea.workshop_count > 1 ? 's' : ''}</Badge>}
                      {idea.stakeholder_count > 0 && <Badge className="text-xs bg-teal-100 text-teal-700"><Users className="h-2.5 w-2.5 mr-0.5" />{idea.stakeholder_count}</Badge>}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{idea.title}</h3>
                    {idea.social_actor_name && (
                      <p className="text-xs font-medium text-emerald-700 mt-0.5 flex items-center gap-1">
                        <Building2 className="h-3 w-3" /> Proposed by <span className="font-semibold">{idea.social_actor_name}</span>
                        {idea.investment_title && <span className="text-gray-500"> · from &ldquo;{idea.investment_title}&rdquo;</span>}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{idea.description}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                      {idea.budget && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{formatBudget(idea.budget)}</span>}
                      {idea.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{idea.location}</span>}
                      <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{idea.comment_count}</span>
                      <span>by {idea.author_name}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 shrink-0 mt-2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Idea Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Investment Idea</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Title *</Label><Input value={newIdea.title} onChange={e => setNewIdea({ ...newIdea, title: e.target.value })} placeholder="e.g., Digital Inclusion for Rural Elders" data-testid="idea-title-input" /></div>
            <div><Label>Description *</Label><Textarea value={newIdea.description} onChange={e => setNewIdea({ ...newIdea, description: e.target.value })} placeholder="Describe the investment idea..." rows={3} data-testid="idea-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category *</Label><Input value={newIdea.category} onChange={e => setNewIdea({ ...newIdea, category: e.target.value })} placeholder="e.g., Digital Inclusion" data-testid="idea-category-input" /></div>
              <div><Label>Budget (EUR)</Label><Input type="number" value={newIdea.budget} onChange={e => setNewIdea({ ...newIdea, budget: e.target.value })} placeholder="50000" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Location</Label><Input value={newIdea.location} onChange={e => setNewIdea({ ...newIdea, location: e.target.value })} placeholder="e.g., Thessaly" /></div>
              <div><Label>Country</Label><Input value={newIdea.country} onChange={e => setNewIdea({ ...newIdea, country: e.target.value })} placeholder="Greece" /></div>
            </div>
            <div>
              <Label>Proposed by organisation (Social Actor)</Label>
              {lockProposedBy ? (
                <div
                  className="w-full mt-1 flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm"
                  data-testid="idea-proposed-by-locked"
                >
                  <span className="font-medium text-slate-800 truncate">
                    {myActor.organization_name}
                    {myActor.city ? <span className="text-slate-500"> · {myActor.city}</span> : null}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">your organisation</span>
                </div>
              ) : (
                <select
                  value={newIdea.social_actor_id || ''}
                  onChange={(e) => setNewIdea({ ...newIdea, social_actor_id: e.target.value })}
                  className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="idea-social-actor-select"
                >
                  <option value="">— Not linked to a registered organisation —</option>
                  {socialActors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.organization_name}{a.city ? ` · ${a.city}` : ''}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {lockProposedBy
                  ? 'New ideas you create are automatically attributed to your organisation. Only administrators or project managers can change this.'
                  : "Linking surfaces the idea on the organisation's profile and the investor matching flow."}
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-2"><Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button><Button onClick={handleCreateIdea} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-idea-btn">Create Idea</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParticipatoryPlanning;
